import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { LoggerService } from '@shared/services/logger.service';
import { Observable, from } from 'rxjs';
import { map, take } from 'rxjs/operators';

export type SessionType = 'login' | 'logout';

export interface RrhhUserSession {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userFirstName?: string;
  userLastName?: string;
  userFullName?: string;
  type: SessionType;
  timestamp: string;
  dateKey: string; // YYYY-MM-DD para agrupar por día
}

@Injectable()
export class RrhhSessionService {
  private readonly afs = inject(AngularFirestore);
  private readonly logger = inject(LoggerService);
  private readonly COLLECTION = 'user_sessions';

  private firstNonEmpty(...values: Array<string | undefined | null>): string {
    for (const value of values) {
      const normalized = (value || '').toString().trim();
      if (normalized.length > 0) {
        return normalized;
      }
    }
    return '';
  }

  /** Firestore no acepta `undefined`; omite esos campos antes de `add`. */
  private stripUndefinedFields<T extends Record<string, unknown>>(obj: T): T {
    const cleaned = {} as Record<string, unknown>;
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned as T;
  }

  /**
   * Registra cuando el usuario inicia sesión en el sistema (login real).
   */
  logLogin(userId: string, userName: string, userEmail: string): void {
    this.logSession(userId, userName, userEmail, 'login');
  }

  /**
   * Registra cuando el usuario cierra sesión en el sistema (logout real).
   */
  logLogout(userId: string, userName: string, userEmail: string): void {
    this.logSession(userId, userName, userEmail, 'logout');
  }

  private logSession(userId: string, userName: string, userEmail: string, type: SessionType): void {
    if (!userId) return;

    const now = new Date();
    const dateKey = this.getDateKey(now);
    const normalizedName = (userName || '').trim();
    const derivedFirstName = this.firstNonEmpty(normalizedName.split(' ')[0]);
    const derivedLastName = this.firstNonEmpty(normalizedName.split(' ').slice(1).join(' '));
    const resolvedFullName = this.firstNonEmpty(normalizedName, [derivedFirstName, derivedLastName].filter(Boolean).join(' '));
    const data = this.stripUndefinedFields({
      userId,
      userName: normalizedName || (userEmail?.includes('@') ? userEmail.split('@')[0] : userEmail) || 'Usuario',
      userEmail: userEmail || 'auth-unavailable',
      userFirstName: derivedFirstName || undefined,
      userLastName: derivedLastName || undefined,
      userFullName: resolvedFullName || undefined,
      type,
      timestamp: now.toISOString(),
      dateKey
    } as Omit<RrhhUserSession, 'id'>);

    const collection = this.afs.collection<RrhhUserSession>(`rrhh_${this.COLLECTION}`);
    collection.add(data).catch(err => {
      this.logger.warn('[RrhhSessionService] Error al registrar sesión:', err);
    });
  }

  /**
   * Elimina todas las sesiones registradas.
   */
  async deleteAllSessions(): Promise<void> {
    const collection = this.afs.collection<RrhhUserSession>(`rrhh_${this.COLLECTION}`);
    const snapshot = await collection.ref.get();
    const batchSize = 500;
    const docs = snapshot.docs;

    for (let i = 0; i < docs.length; i += batchSize) {
      const batch = this.afs.firestore.batch();
      const chunk = docs.slice(i, i + batchSize);
      chunk.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
    }
  }

  /**
   * Obtiene sesiones en un rango de fechas (dateKey YYYY-MM-DD).
   * Ordenadas por dateKey descendente. Lectura puntual vía `Query.get()` (sin listener).
   */
  getSessionsByDateRange(startDateKey: string, endDateKey: string, limit = 5000): Observable<RrhhUserSession[]> {
    const q = this.afs.collection<RrhhUserSession>(`rrhh_${this.COLLECTION}`, ref =>
      ref.where('dateKey', '>=', startDateKey).where('dateKey', '<=', endDateKey).orderBy('dateKey', 'desc').limit(limit)
    );
    return from(q.ref.get()).pipe(
      take(1),
      map((snapshot) =>
        snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id } as RrhhUserSession))
      )
    );
  }

  /**
   * Obtiene sesiones para el panel de Auditoría. Lectura puntual vía `Query.get()` (sin listener).
   */
  getSessions(limit = 100): Observable<RrhhUserSession[]> {
    const q = this.afs.collection<RrhhUserSession>(`rrhh_${this.COLLECTION}`, ref =>
      ref.orderBy('timestamp', 'desc').limit(limit)
    );
    return from(q.ref.get()).pipe(
      take(1),
      map((snapshot) =>
        snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id } as RrhhUserSession))
      )
    );
  }

  private getDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
